/**
 * memberDisplayName — tolerant display-name reader for account member docs.
 *
 * Member docs are seeded three ways — the Firestore console (fields typed by
 * hand), `addMemberToAccount`, and bulk imports — so the display-name field
 * name/casing drifts in practice (`displayName`, `display_name`, `displayname`,
 * `name`, `fullName`, or the same with a stray trailing space / odd casing).
 * The add-people picker keys its label on `displayName`; when the stored field is
 * mis-keyed the label silently falls back to the raw email (the exact bug this
 * fixes). `readDisplayName` accepts any non-empty variant — exact match first,
 * then a normalized key scan — and trims it, returning undefined only when no
 * usable string is present (the caller then falls back to email → uid).
 *
 * Pure + dependency-free (no firebase) so it unit-tests in isolation.
 */

/** Exact field names tried first, in priority order. */
export const DISPLAY_NAME_KEYS = [
  'displayName',
  'display_name',
  'displayname',
  'name',
  'fullName',
  'full_name',
] as const;

/** Normalized aliases accepted by the tolerance pass (odd casing / spaced names). */
const DISPLAY_NAME_ALIASES = new Set<string>([
  'displayname',
  'display_name',
  'name',
  'fullname',
  'full_name',
]);

/** lowercase + trim + collapse internal whitespace to `_` ("Display Name" → "display_name"). */
function normalizeKey(key: string): string {
  return key.trim().toLowerCase().replace(/\s+/g, '_');
}

export function readDisplayName(raw: Record<string, unknown> | null | undefined): string | undefined {
  if (!raw) return undefined;

  // Exact-key pass — the common, well-formed shapes.
  for (const key of DISPLAY_NAME_KEYS) {
    const value = raw[key];
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
    }
  }

  // Tolerance pass — odd casing / trailing spaces / spaced field NAMES that an
  // exact-key lookup misses (e.g. "displayName ", "Display Name", "DISPLAYNAME").
  for (const [key, value] of Object.entries(raw)) {
    if (typeof value !== 'string') continue;
    if (!DISPLAY_NAME_ALIASES.has(normalizeKey(key))) continue;
    const trimmed = value.trim();
    if (trimmed) return trimmed;
  }

  return undefined;
}

/** A member as seen by the label map — uid plus whatever name/email we have. */
export interface MemberLabelSource {
  userId?: string;
  displayName?: string;
  email?: string;
}

/**
 * Assemble the `uid → display label` map used for group-thread sender
 * attribution and avatars.
 *
 * Priority (first non-empty wins; every later source only FILLS gaps and never
 * overrides a name an earlier source already resolved):
 *   1. account members — the roster (`displayName` then `email`);
 *   2. the account owner (from the profile) — a resilient label for the owner
 *      even before their member doc is seeded, so the workspace owner never
 *      degrades to a "Teammate <uid>" tag;
 *   3. the signed-in user themselves — so a participant always at least sees
 *      their OWN real name.
 *
 * Pure + dependency-free (no firebase) so it unit-tests in isolation.
 */
export function buildMemberLabelMap(sources: {
  members?: ReadonlyArray<MemberLabelSource> | null;
  owner?: { uid?: string; label?: string } | null;
  self?: MemberLabelSource | null;
}): Record<string, string> {
  const map: Record<string, string> = {};
  const put = (uid: string | undefined, label: string | undefined): void => {
    const u = (uid || '').trim();
    const l = (label || '').trim();
    if (u && l && !map[u]) map[u] = l;
  };
  for (const m of sources.members || []) {
    put(m?.userId, (m?.displayName || m?.email || '').trim());
  }
  if (sources.owner) put(sources.owner.uid, sources.owner.label);
  if (sources.self) put(sources.self.userId, (sources.self.displayName || sources.self.email || '').trim());
  return map;
}
